import { useEffect, useMemo, useState } from "react";
import { api, fmtBps, pctToDec, decToPct, isYmd, todayYmd } from "@/lib/api";
import { PageHeader, Btn, Modal, Input, Section, Select, ComboSelect } from "@/components/Shared";
import { Plus, Pencil, Trash2, X, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import RateHistoryTab from "@/components/RateHistoryTab";
import RateChangeDialog from "@/components/RateChangeDialog";
import ModalTabs from "@/components/ModalTabs";
import { toast } from "sonner";
import { openConfirm } from "@/lib/dialogs";

const EMPTY = { gateway_name: "", notes: "", lob_rates: [] };

// Stable client-only id for editable lob_rate rows so React's reconciliation
// survives addRow/removeRow without input-state shifting between rows.
// Server never sees this field — it's stripped in the save payload below.
const _newRowUid = () => `lob_${Math.random().toString(36).slice(2, 10)}`;

export default function Gateways() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ lobs: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const [filterLob, setFilterLob] = useState("");
  const [sortBy, setSortBy] = useState({ key: "gateway_name", dir: "asc" });
  // PR-A.5 — modal tabs (form / rate-history) + scalar-rate-change dialog.
  const [modalTab, setModalTab] = useState("form");
  const [rateHistoryLob, setRateHistoryLob] = useState("");
  const [rateChangePending, setRateChangePending] = useState(null);

  const load = async () => {
    const [g, m] = await Promise.all([api.get("/gateways"), api.get("/meta")]);
    setRows(g.data); setMeta(m.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!String(form.gateway_name || "").trim()) {
      toast.error("Please fill: Gateway Name");
      return;
    }
    try {
      // UI accepts % (e.g. 0.92); convert to decimal (0.0092) before POST
      const payload = { ...form, lob_rates: form.lob_rates.filter((r) => r.lob && r.lob.trim()).map((r) => ({ lob: r.lob.trim(), base_rate: pctToDec(r.base_rate) })) };

      // PR-A.5 — On edit, detect base-rate changes and surface the
      // rate-change dialog. Single-LOB change → versioned; multi-LOB →
      // ALSO surfaces the dialog so admin picks ONE effective_from that
      // applies to every changed LOB (one rate-version row per LOB).
      // Versioning was previously skipped for multi-LOB which silently
      // overwrote scalars without an audit trail (iter28 fix).
      if (editId) {
        const oldRow = rows.find((g) => g.gateway_name === editId);
        const oldRates = oldRow?.lob_rates || [];
        const changes = [];
        for (const nr of payload.lob_rates) {
          const oc = oldRates.find((r) => r.lob === nr.lob);
          if (oc && Number(oc.base_rate) !== Number(nr.base_rate)) {
            changes.push({ lob: nr.lob, oldRate: Number(oc.base_rate), newRate: Number(nr.base_rate) });
          }
        }
        if (changes.length >= 1) {
          // For single-LOB we keep `lob`/`oldRate`/`newRate` at the top
          // level so RateChangeDialog can show side-by-side rates. For
          // multi-LOB we pass `changes` and the dialog renders a list.
          const first = changes[0];
          setRateChangePending({
            payload, gatewayName: editId,
            lob: first.lob, oldRate: first.oldRate, newRate: first.newRate,
            changes,  // full list — confirmRateChange iterates this
          });
          setRateHistoryLob(first.lob);
          return;
        }
      }
      if (editId) await api.put(`/gateways/${editId}`, payload); else await api.post("/gateways", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // PR-A.5 — Persist gateway edit + post a gateway_lob rate version.
  // For FUTURE-DATED rate changes the gateway's scalar `lob_rates[].base_rate`
  // is intentionally NOT updated yet — the current display rate stays at the
  // OLD value until the effective date arrives. The calc engine reads via
  // rate_at(), so the right rate is used per period regardless of scalar.
  const confirmRateChange = async ({ effective_from, reason }) => {
    if (!rateChangePending) return;
    const { payload, gatewayName, changes } = rateChangePending;
    if (!isYmd(effective_from)) {
      toast.error("Invalid effective date — expected YYYY-MM-DD");
      return;
    }
    const isFuture = effective_from > todayYmd();
    try {
      // Build the gateway PUT body. For future-dated changes, restore the
      // OLD rate on EACH changed LOB so the directory page stays accurate
      // until the effective date arrives.
      let putBody = payload;
      if (isFuture) {
        const restoreByLob = Object.fromEntries(changes.map((c) => [c.lob, Number(c.oldRate)]));
        putBody = {
          ...payload,
          lob_rates: payload.lob_rates.map((r) =>
            r.lob in restoreByLob ? { ...r, base_rate: restoreByLob[r.lob] } : r
          ),
        };
      }
      await api.put(`/gateways/${gatewayName}`, putBody);
      // Post one rate-version row per changed LOB. Sequential — keeps
      // the audit log in deterministic order.
      for (const c of changes) {
        await api.post("/rate-versions", {
          scope: "gateway_lob",
          gateway: gatewayName,
          lob: c.lob,
          effective_from: `${effective_from}T00:00:00Z`,
          rate_type: "Basis Points",
          rate_value: Number(c.newRate),
          reason: reason || `Gateway base rate change · ${c.lob}`,
        });
      }
      const summary = changes.length === 1
        ? `${changes[0].lob}`
        : `${changes.length} LOBs (${changes.map((c) => c.lob).join(", ")})`;
      if (isFuture) {
        toast.success(`Future-dated rate version saved · activates on ${effective_from}`, {
          description: `${summary} — current display rates stay unchanged until then. Calculations after the effective date will use the new rates automatically.`,
          duration: 7000,
        });
      } else {
        toast.success(`Saved · ${summary} updated effective ${effective_from}`);
      }
      setRateChangePending(null);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const remove = async (id) => {
    if (!await openConfirm({ title: "Delete gateway?", message: "Any merchants/programs that reference this gateway will keep their existing rates, but you won't be able to assign it to new ones.", confirmLabel: "Delete gateway", danger: true })) return;
    await api.delete(`/gateways/${id}`); toast.success("Deleted"); load();
  };

  const addLobRate = () => setForm({ ...form, lob_rates: [...form.lob_rates, { _uid: _newRowUid(), lob: "", base_rate: 0 }] });
  const updateLobRate = (i, field, val) => {
    const next = [...form.lob_rates]; next[i] = { ...next[i], [field]: val }; setForm({ ...form, lob_rates: next });
  };
  const removeLobRate = (i) => setForm({ ...form, lob_rates: form.lob_rates.filter((_, j) => j !== i) });

  const openEdit = (g) => {
    setForm({ ...g, lob_rates: (g.lob_rates || []).map((r) => ({ _uid: _newRowUid(), lob: r.lob, base_rate: decToPct(r.base_rate) })) });
    setEditId(g.gateway_name);
    setModalTab("form");
    setRateHistoryLob((g.lob_rates || [])[0]?.lob || "");
    setOpen(true);
  };

  const toggleSort = (key) => {
    setSortBy((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  };
  const SortHeader = ({ k, children }) => {
    const active = sortBy.key === k;
    const Icon = !active ? ArrowUpDown : (sortBy.dir === "asc" ? ArrowUp : ArrowDown);
    return (
      <th>
        <button type="button" data-testid={`gw-sort-${k}`} onClick={() => toggleSort(k)}
          className={`inline-flex items-center gap-1 hover:text-[var(--ink)] ${active ? "text-[var(--ink)]" : ""}`}>
          <span>{children}</span>
          <Icon size={11} strokeWidth={2} className={active ? "text-[var(--accent)]" : "text-[var(--muted)]"} />
        </button>
      </th>
    );
  };

  const filtered = useMemo(() => {
    let list = rows.filter((g) => {
      if (q && !g.gateway_name.toLowerCase().includes(q.toLowerCase())) return false;
      if (filterLob && !(g.lob_rates || []).some((r) => r.lob === filterLob)) return false;
      return true;
    });
    const { key, dir } = sortBy;
    const mul = dir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      if (key === "num_lobs") return ((a.lob_rates?.length || 0) - (b.lob_rates?.length || 0)) * mul;
      return String(a[key] || "").localeCompare(String(b[key] || "")) * mul;
    });
    return list;
  }, [rows, q, filterLob, sortBy]);

  const clearFilters = () => { setQ(""); setFilterLob(""); };
  const hasFilters = q || filterLob;

  return (
    <div data-testid="gateways-page">
      <PageHeader
        title="Payment Gateways"
        actions={<Btn variant="accent" testid="add-gateway-btn" onClick={() => { setForm(EMPTY); setEditId(null); setModalTab("form"); setOpen(true); }}><Plus size={14} /> New Gateway</Btn>}
      />
      <div className="p-4 sm:p-10">
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="gw-filter-bar">
          <div className="w-56"><Input placeholder="Search gateways…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="gw-search" /></div>
          <div className="w-48">
            <select
              data-testid="gw-filter-lob"
              value={filterLob}
              onChange={(e) => setFilterLob(e.target.value)}
              className="w-full h-9 px-3 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)]"
            >
              <option value="">All LOBs</option>
              {(meta.lobs || []).map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          {hasFilters && <Btn variant="ghost" testid="gw-clear-filters" onClick={clearFilters}>Clear filters</Btn>}
        </div>

        <Section title={`${filtered.length} gateway${filtered.length === 1 ? "" : "s"}${hasFilters ? " · filtered" : ""}`} testid="gateways-section">
          <div className="overflow-x-auto"><table className="table-data w-full">
            <thead><tr>
              <SortHeader k="gateway_name">Gateway</SortHeader>
              <th>LOB Rates</th>
              <SortHeader k="num_lobs">LOBs</SortHeader>
              <th>Notes</th><th></th>
            </tr></thead>
            <tbody>
              {filtered.map((g) => (
                <tr key={g.gateway_name} data-testid={`gateway-row-${g.gateway_name}`}>
                  <td className="font-medium">{g.gateway_name}</td>
                  <td>
                    <div className="flex flex-wrap gap-1.5">
                      {(g.lob_rates || []).map((r, i) => (
                        <span key={`${r.lob}-${i}`} className="inline-flex items-center gap-2 bg-[var(--surface-2)] border border-[var(--line-2)] rounded-sm px-2 py-1 text-[11px]">
                          <span>{r.lob}</span>
                          <span className="num font-semibold text-[var(--ink)]">{fmtBps(r.base_rate)}</span>
                        </span>
                      ))}
                      {!g.lob_rates?.length && <span className="text-[var(--muted)] text-[11px]">No rates set</span>}
                    </div>
                  </td>
                  <td className="num text-[12px] text-[var(--muted)]">{g.lob_rates?.length || 0}</td>
                  <td className="text-[11px] text-[var(--muted)] max-w-md">{g.notes || "—"}</td>
                  <td className="text-right">
                    <button onClick={() => openEdit(g)} className="p-1.5 hover:bg-[var(--surface-2)] rounded-sm"><Pencil size={13} /></button>
                    <button onClick={() => remove(g.gateway_name)} className="p-1.5 hover:bg-red-50 text-[var(--negative)] rounded-sm"><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
              {!filtered.length && <tr><td colSpan={5} className="text-center text-[var(--muted)] py-6">{hasFilters ? "No gateways match the current filters." : "No gateways yet."}</td></tr>}
            </tbody>
          </table></div>
        </Section>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Gateway" : "New Gateway"} testid="gateway-modal" size="lg">
        {editId && (
          <ModalTabs
            testid="gateway-modal-tabs"
            active={modalTab}
            onChange={setModalTab}
            tabs={[
              { id: "form", label: "Details", testid: "gateway-tab-form" },
              { id: "rate-history", label: "Rate History", testid: "gateway-tab-rate-history" },
            ]}
          />
        )}
        {modalTab === "rate-history" && editId ? (
          <div className="space-y-3">
            <div className="text-[12px] text-[var(--muted)]">
              Pick an LOB to view its base-rate version timeline for {editId}.
            </div>
            <div>
              <label className="text-[10.5px] uppercase text-[var(--muted)] font-semibold tracking-wider">LOB</label>
              <Select
                testid="gateway-rh-lob"
                placeholder="Select LOB"
                options={Array.from(new Set((form.lob_rates || []).map((r) => r.lob).filter(Boolean)))}
                value={rateHistoryLob}
                onChange={setRateHistoryLob}
              />
            </div>
            <RateHistoryTab
              scope="gateway_lob"
              keyFields={{ gateway: editId, lob: rateHistoryLob }}
              currentRate={pctToDec((form.lob_rates || []).find((r) => r.lob === rateHistoryLob)?.base_rate || 0)}
            />
          </div>
        ) : (
        <div className="grid gap-3">
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Gateway Name <span className="text-[var(--negative)]">*</span></label>
            <Input data-testid="form-gw-name" value={form.gateway_name} onChange={(e) => setForm({ ...form, gateway_name: e.target.value })} disabled={!!editId} placeholder="e.g. Razorpay" /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Notes</label>
            <Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Internal notes" /></div>
          <div className="border-t border-[var(--line)] pt-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Base rate per LOB (%)</label>
              <Btn variant="ghost" testid="add-lob-rate" onClick={addLobRate}><Plus size={12} /> Add row</Btn>
            </div>
            <div className="space-y-2">
              {form.lob_rates.map((r, i) => (
                <div key={r._uid || `lob-row-${i}`} className="grid grid-cols-[1fr,140px,32px] gap-2 items-center">
                  <ComboSelect
                    options={meta.lobs || []}
                    value={r.lob}
                    onChange={(v) => updateLobRate(i, "lob", v)}
                    placeholder="LOB"
                    testid={`lob-rate-lob-${i}`}
                  />
                  <div className="relative">
                    <Input type="number" step="0.01" placeholder="0.92" value={r.base_rate}
                      onChange={(e) => updateLobRate(i, "base_rate", e.target.value)} data-testid={`lob-rate-rate-${i}`} className="pr-7" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[12px] text-[var(--muted)] pointer-events-none">%</span>
                  </div>
                  <button type="button" onClick={() => removeLobRate(i)} className="text-[var(--muted)] hover:text-[var(--negative)] p-1.5"><X size={14} /></button>
                </div>
              ))}
              {!form.lob_rates.length && <div className="text-[12px] text-[var(--muted)]">Add base rates for each LOB this PG handles. Use "+ Add new…" inside the dropdown to define a new LOB (e.g. "Toll Plus") — it'll become selectable everywhere.</div>}
            </div>
          </div>
        </div>
        )}
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
          {modalTab === "form" && <Btn variant="accent" testid="save-gateway" onClick={save}>{editId ? "Save changes" : "Create gateway"}</Btn>}
        </div>
      </Modal>

      <RateChangeDialog
        open={!!rateChangePending}
        onClose={() => setRateChangePending(null)}
        onConfirm={confirmRateChange}
        oldRate={rateChangePending?.oldRate}
        newRate={rateChangePending?.newRate}
        changes={rateChangePending?.changes}
        entityLabel={rateChangePending ? `Gateway ${rateChangePending.gatewayName}${rateChangePending.changes?.length === 1 ? ` · ${rateChangePending.lob}` : ""}` : ""}
      />
    </div>
  );
}
